import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { logAction } from "../log/actionLog";
import { euros } from "./articles";
import {
  fetchInvoices,
  fileName,
  downloadInvoice,
  type Invoice,
} from "./invoices";
import { useParticipation } from "./useParticipation";
import { useLocale, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";

/**
 * LES FACTURES, À LA CONFIRMATION.
 *
 * ⚠️ RIEN NE S'AFFICHE S'IL N'Y EN A PAS, et c'est le cas de l'immense majorité des
 * participants : un titre « Vos factures » au-dessus du vide donne à croire qu'un
 * document manque.
 *
 * ⚠️ ET SEULEMENT LES RÉGLÉES. La route ne rend que celles-là — voir `funnel/invoices.ts`.
 * Une facture en attente n'atteste de rien.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

export function InvoiceList({ active }: { active: boolean }) {
  const t = useT();
  const event = useEvent();
  const { me } = useParticipation();
  const title = useText("FO_MEETING_V2_INVOICES_TITLE", "Vos factures");

  const locale = useLocale();

  const q = useQuery({
    queryKey: ["factures", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchInvoices(event!.domainId, event!.eventId, signal),
    enabled: active && !!event,
    /* Une facture de plus n'apparaît qu'après un paiement, et un paiement fait
       revenir sur cet écran par une navigation complète. */
    staleTime: Infinity,
    retry: false,
  });

  /** Celle qu'on est en train de préparer — le PDF se fabrique à la demande. */
  const [inFlight, setInFlight] = useState<number | null>(null);
  const [rate, setRate] = useState(false);

  const invoices = q.data ?? [];
  if (!active || invoices.length === 0) return null;

  /**
   * ⚠️ LE NOM DU FICHIER SE FORCE CÔTÉ CLIENT. Une adresse de blob ne porte AUCUN nom :
   * ouverte telle quelle, elle donne un fichier appelé comme un identifiant aléatoire, et
   * l'en-tête `Content-Disposition` du serveur ne survit pas à la conversion en blob.
   * C'est l'attribut `download` d'un lien qui nomme — leçon déjà payée sur le badge.
   *
   * ⚠️ ET L'ADRESSE SE RÉVOQUE. Chaque blob retient son contenu en mémoire tant qu'on ne
   * le libère pas ; sur un écran où l'on télécharge trois factures, autant de PDF
   * resteraient là pour la durée de la visite.
   */
  const download = async (invoice: Invoice) => {
    if (!event || typeof me?.id !== "number" || inFlight !== null) return;
    setInFlight(invoice.id);
    setRate(false);
    try {
      const blob = await downloadInvoice(
        event.domainId,
        event.eventId,
        me.id,
        invoice,
        locale,
      );
      const address = URL.createObjectURL(blob);
      const lien = document.createElement("a");
      lien.href = address;
      lien.download = fileName(invoice, t("INVOICE_FILE", "Facture"));
      document.body.appendChild(lien);
      lien.click();
      lien.remove();
      URL.revokeObjectURL(address);
      logAction(event, "invoiceDownload", { label: `Facture ${invoice.numero}` });
    } catch {
      setRate(true);
    } finally {
      setInFlight(null);
    }
  };

  return (
    <div className="invs" data-cfg="confirmationInvoices">
      <div className="rsec">{title}</div>

      {rate && (
        <p className="loginerr" role="alert">
          {t(
            "INVOICE_NOT_PREPARED",
            "La facture n'a pas pu être préparée. Réessayez, et demandez-la à l'organisateur si cela persiste.",
          )}
        </p>
      )}

      {invoices.map((invoice) => (
        <div className="inv" key={invoice.id}>
          <span className="inv__t">
            <b>{t("INVOICE_N", "Facture $1", [invoice.numero])}</b>
            <small>
              {t("AMOUNT_INCL_TAX", "$1 TTC", [euros(invoice.amountInclTax, locale)])}
              {invoice.date && ` · ${invoice.date}`}
            </small>
          </span>
          <Button
            type="button"
            variant="ghost"
            loading={inFlight === invoice.id}
            onClick={() => void download(invoice)}
          >
            {t("DOWNLOAD", "Télécharger")}
          </Button>
        </div>
      ))}
    </div>
  );
}
